import { BindAll } from 'lodash-decorators'
import type { SearchForm } from './types.d'
import type { Dictionary } from 'vue-router/types/router'
import { myCourseMenuFilter } from '@xt/client/dataSource/my/index'
import { action, computed, observable, toJS } from 'mobx'
import type { CourseItem, CourseType, MyCourseList } from '@xt/client/types/my-go-course'
import lodash from 'lodash'
import { EnumMyCourseMenu, EnumContentType, EnumProductType } from '@xt/client/enums/my'
import { $service } from '@xt/client/experimental/request/service'
import { reportEntryLog } from '@xt/client/utils/log'
import { EnumApiMy } from '@xt/client/api/my'

export type RouteQuery = Dictionary<string | (string | null)[]>

type MyProductStatus = {
  loading: boolean
  isError: boolean
  haveNoneCourse: boolean
}

@BindAll()
export class MyCourseController {
  @observable myProductList: MyCourseList | null = null
  @observable filterList: Array<CourseItem> = []
  @observable myProductStatus: MyProductStatus = {
    loading: true,
    isError: false,
    haveNoneCourse: false
  }

  get mapList() {
    const result: Array<CourseItem & { type: CourseType }> = []
    const courseTypeMap = {
      courseResultVOS: 'course',
      activityResultVOS: 'daycourse',
      showResultVOS: 'exhibition',
      selfStudyResultVOS: 'selfstudy',
      specialCourseResults: 'specialCourse',
      remedialShowResults: 'exhibition'
    }
    const myProductList = toJS(this.myProductList)

    if (!myProductList) return []

    Object.entries(courseTypeMap).forEach(([fieldName, value]) => {
      if (Array.isArray(myProductList[fieldName])) {
        myProductList[fieldName].forEach((course: CourseItem) => {
          result.push({ ...course, type: value as CourseType })
        })
      }
    })

    return result.sort((a, b) => b.successTime - a.successTime)
  }

  async loadMyProductList(query: RouteQuery) {
    try {
      this.setMyProductStatus({
        loading: true
      })

      const [err, res] = await $service<MyCourseList>({
        url: EnumApiMy.MyCourseList,
        params: {
          all: true
        }
      })

      if (err) throw err

      this.setMyProductList(res)

      if (this.myProductList) {
        this.setMyProductStatus({
          haveNoneCourse: this.mapList.length === 0
        })
      }
      this.setMyProductStatus({
        isError: false
      })
    } catch (error) {
      reportEntryLog('toclass.loading-data.fail', { error })
      this.setMyProductStatus({
        isError: true
      })
    } finally {
      this.setMyProductStatus({
        loading: false
      })
    }
  }

  mapContentType(contentType: EnumMyCourseMenu, type: CourseType) {
    let filter: CourseType[] = []

    switch (contentType) {
      case EnumMyCourseMenu.Teach:
        filter = ['course', 'specialCourse']
        break
      case EnumMyCourseMenu.Product:
        filter = ['daycourse', 'selfstudy', 'exhibition']
        break
      case EnumMyCourseMenu.Offline:
        break
      default:
        break
    }

    return filter.includes(type)
  }

  mapCourseType(courseType: EnumContentType | EnumProductType, item: CourseItem & { type: CourseType }) {
    if (!courseType) return true

    return item.contentType.toString() === courseType
  }

  mapProductType(courseType: EnumContentType | EnumProductType, type: CourseType) {
    switch (courseType) {
      case EnumProductType.Series:
        return type === 'course'
      case EnumProductType.SpecialTopic:
        return type === 'specialCourse'
      case EnumProductType.DayCourse:
        return type === 'daycourse'
      case EnumProductType.SelfStudy:
        return type === 'selfstudy'
      case EnumProductType.Exhibition:
        return type === 'exhibition'
      default:
        return false
    }
  }

  changeFilterList(filter: { contentType: string; courseType: string }) {
    const { contentType, courseType } = filter

    const list = lodash.filter(this.mapList, item => {
      if (!contentType) {
        return this.mapCourseType(courseType as EnumContentType | EnumProductType, item)
      }
      if (!courseType) {
        return this.mapContentType(contentType as EnumMyCourseMenu, item.type)
      }
      return this.mapProductType(courseType as EnumContentType | EnumProductType, item.type)
    })

    this.setFilterList(list)
  }

  @action
  setMyProductStatus(status: Partial<MyProductStatus>) {
    this.myProductStatus = {
      ...this.myProductStatus,
      ...status
    }
  }

  @action
  setFilterList(list: Array<CourseItem>) {
    this.filterList = [...list]
  }

  @action
  setMyProductList(list: MyCourseList) {
    this.myProductList = {
      ...list
    }
  }

  getSeachListByFieldName(fieldName: keyof SearchForm, routerQuery: RouteQuery) {
    switch (fieldName) {
      case 'contentType':
        return myCourseMenuFilter
      case 'courseType':
        const parseField = this.getFieldParser(routerQuery)
        return myCourseMenuFilter.find(item => item.value === parseField('contentType'))?.filters || []
      default:
        return []
    }
  }

  getFieldParser(routerQuery: Dictionary<string | (string | null)[]>) {
    return (fieldName: keyof SearchForm, defultValue: string = '') => {
      const fieldValue = routerQuery[fieldName]

      if (typeof fieldValue !== 'string') return ''

      return this.getSeachListByFieldName(fieldName, routerQuery).find(i => i.value.toString() === fieldValue)?.value || defultValue
    }
  }

  // 获取路由参数
  public parserQuery(routerQuery: Dictionary<string | (string | null)[]>): SearchForm {
    const parseField = this.getFieldParser(routerQuery)

    return {
      contentType: parseField('contentType'),
      courseType: parseField('courseType')
    }
  }

  // 获取查询query值
  public toSearchQuery(form: SearchForm): Record<keyof SearchForm, string> {
    const obj = {} as Record<keyof SearchForm, string>

    Object.keys(form).forEach(key => {
      if (form[key].toString() !== '') {
        obj[key] = form[key].toString()
      }
    })

    return obj
  }

  @action
  reset() {
    this.myProductList = null
    this.filterList = []
  }
}
